import { createContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useParams } from 'react-router';
import { userAxios, userBookingAxios } from 'APIs';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

interface UsersDetail {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
  city: string;
  region: string;
  zip: string;
  image?: string; 
  dateOfBirth?: string; 
  gender?: 'Male' | 'Female' | 'Others'; 
  status?: number; 
  role?: string;   
}

interface UserBookingDetails {
  address: string;
  chef: string;
  startDate: string;
  startTime: string;
  requirement: string;
  meals: string;
  headCount: string;
  additionalServices: string;
  baseFare: string;
  coupon: string;
  description: string;
  finalFare: string;
}

interface userDetail {
  isLoading: boolean;
  status: number;
  error: boolean;
  data: UsersDetail;
}

interface userBookingDetails {
  isLoading: boolean;
  status: number;
  error: boolean;
  data: any;
}

interface updateBooking {
  isLoading: boolean;
  status: number;
  error: boolean;
  data: UserBookingDetails;
}

export interface BookingControlState {
  openChefPopover: number | null;
  ellipsisPopover: number | null;
  openRow: number | null;
  openPopoverIndex: number | null;
  currentPage: number;
}

export interface UsersContextProps {
  userDetail: userDetail;
  userBookings: userBookingDetails;
  updateBooking: (
    bookingId: string,
    newDate: string,
    meals: string,
    additionalServices: string,
    status: string,
    startTime?: string,
    endTime?: string
  ) => Promise<void>;
  userUpdateBooking: updateBooking;
  updateBookingState: updateBooking;

  showRescheduleModal: boolean;
  setShowRescheduleModal: (value: boolean) => void;
  rescheduleBooking: any;
  setRescheduleBooking: (value: any) => void;
  showCancelPop: boolean;
  setShowCancelPop: (value: boolean) => void;
  cancelBooking: any;
  setCancelBooking: (value: any) => void;
  setUserUpdateBooking: React.Dispatch<React.SetStateAction<updateBooking>>;

  bookingControlState: BookingControlState;
  setBookingControlState: React.Dispatch<
    React.SetStateAction<BookingControlState>
  >;
}

const USER_BOOKINGS_DATA_MODAL: updateBooking = {
  isLoading: false,
  status: 0,
  error: false,
  data: {
    address: '',
    chef: '',
    startDate: '',
    startTime: '',
    requirement: '',
    meals: '',
    headCount: '',
    additionalServices: '',
    baseFare: '',
    coupon: '',
    description: '',
    finalFare: '',
  },
};

const USER_DATA_MODAL: userDetail = {
  isLoading: true,
  status: 0,
  error: false,
  data: {
    id: '',
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    city: '',
    region: '',
    zip: '',
  },
};

export const UsersContextDetail = createContext<UsersContextProps | undefined>(
  undefined
);

export const UsersDetailProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [userDetail, setUserData] = useState<userDetail>(USER_DATA_MODAL);
  const [userBookings, setUserBookings] = useState<userBookingDetails>({
    isLoading: true,
    status: 0,
    error: false,
    data: [],
  });
  const [userUpdateBooking, setUserUpdateBooking] = useState<updateBooking>(
    USER_BOOKINGS_DATA_MODAL
  );
  console.log('setUserUpdateBooking', userUpdateBooking);
  const [updateBookingState, setUpdateBookingState] = useState<updateBooking>(
    USER_BOOKINGS_DATA_MODAL
  );


  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleBooking, setRescheduleBooking] = useState<any>(null);
  const [showCancelPop, setShowCancelPop] = useState(false);
  const [cancelBooking, setCancelBooking] = useState<any>(null);

  const [bookingControlState, setBookingControlState] =
    useState<BookingControlState>({
      openChefPopover: null,
      ellipsisPopover: null,
      openRow: null,
      openPopoverIndex: null,
      currentPage: 1,
    });

  const navigate = useNavigate();

  const { filters } = useSelector((state: any) => state.customerDetails);

  const { customerId } = useParams();

  const hasFilterSelected =
    filters &&
    Object.keys(filters).some((key) => {
      const value = filters[key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== '' && value !== null && value !== undefined;
    });

  const fetchUserData = useCallback( async () => {
    try {
      const response = await userAxios.get(`/users/${customerId}`);
      const {
        data: { error, data },
        status,
      } = response;

      setUserData({
        isLoading: false,
        status: status,
        error: error,
        data: data.user,
      });
    } catch (err) {
      console.error('Error fetching user data', err);
    }
  },[customerId]);

  const fetchBookingData = useCallback( async () => {
    try {
      const response = await userBookingAxios.get(`/bookings`, {
        headers: {
          userId: customerId,
          offset: '',
          limit: '',
          search: '',
          filter: '',
        },
      });
      const {
        data: { error, data },
        status,
      } = response;

      setUserBookings({
        isLoading: false,
        status: status,
        error: error,
        data: data,
      });
    } catch (error) {
      console.error('Booking error', error);
    }
  },[customerId]);

  useEffect(() => {
    fetchUserData();
    fetchBookingData();
  }, [fetchUserData, fetchBookingData]);

  const updateBooking = async (
    bookingId: string,
    newDate: string,
    meals: string,
    additionalServices: string,
    statusBooking: string,
    startTime?: string,
    endTime?: string
  ) => {
    setUpdateBookingState({
      ...USER_BOOKINGS_DATA_MODAL,
      isLoading: true,
    });

    console.log('context details data', bookingId, statusBooking);

    try {
      const response = await userBookingAxios.patch(
        `/bookings/${bookingId}`,
        {
          startDate: newDate,
          meals: meals || '',
          additionalServices: additionalServices || '',
          status: statusBooking || '',
          startTime: startTime,
          endTime: endTime,
          endDate: newDate,
        },
        {
          headers: {
            userId: customerId,
          },
        }
      );

      const {
        data: { error, data },
        status,
      } = response;

      setUserBookings((prev) => {
        const updatedBookings = [...(prev.data.bookings || [])];

        updatedBookings.forEach((obj, index) => {
          if (obj.id === bookingId) {
            updatedBookings[index] = {
              ...obj,
              startDate: newDate,
              startTime: startTime,
              endTime: endTime,
              endDate: newDate,
              status: statusBooking,
            };
          }
        });

        return {
          ...prev,
          data: {
            ...prev.data,
            bookings: updatedBookings,
          },
        };
      });

      setUpdateBookingState({
        isLoading: false,
        status,
        error,
        data,
      });

      console.log('✅ Booking updated:x`', updateBookingState);
    } catch (error) {
      console.error('❌ Failed to update booking:', error);

      if(axios.isAxiosError(error)){
        const status = error.response?.status;

        if(status === 401){
          localStorage.clear();
          navigate('/')
        }

      }

      setUpdateBookingState({
        isLoading: false,
        status: 500,
        error: true,
        data: USER_BOOKINGS_DATA_MODAL.data,
      });
    }
  };

  useEffect(() => {
    if (!hasFilterSelected) {
      return;
    }
    fetchBookingData();
  }, [hasFilterSelected,fetchBookingData]);

  return (
    <UsersContextDetail.Provider
      value={{
        userDetail,
        userBookings,
        updateBooking,
        userUpdateBooking,
        updateBookingState,
        showRescheduleModal,
        setShowRescheduleModal,
        rescheduleBooking,
        setRescheduleBooking,
        showCancelPop,
        setShowCancelPop,
        cancelBooking,
        setCancelBooking,
        bookingControlState,
        setBookingControlState,
        setUserUpdateBooking
      }}
    >
      {children}
    </UsersContextDetail.Provider>
  );
};
